import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import axios from '../../services/axios';
import WebSocketService from '../../services/WebSocketService';

// Aviso que aparece quando o agente assume um atendimento no chat. O evento
// chega pelo WebSocket já direcionado a este usuário — o backend descobre quem
// é pelo e-mail do agente no evento do chatbot.
export default function ChatHandoffModal() {
  const userId = useSelector((state) =>
    state.auth.user ? state.auth.user.id : null
  );
  const navigate = useNavigate();

  const [handoff, setHandoff] = useState(null);
  const [requesters, setRequesters] = useState([]);
  const [selectedRequesterId, setSelectedRequesterId] = useState('');
  const [isLoadingRequesters, setIsLoadingRequesters] = useState(false);
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDeskId, setSelectedServiceDeskId] = useState('');
  const [isLoadingDesks, setIsLoadingDesks] = useState(false);

  const solicitanteEscolhido = requesters.find(
    (item) => String(item.id) === String(selectedRequesterId)
  );
  const empresaEscolhidaId = solicitanteEscolhido
    ? solicitanteEscolhido.companyId
    : null;

  // Busca o solicitante pelo telefone do contato. O mesmo telefone pode existir
  // em mais de uma empresa: nesse caso o agente escolhe qual antes de seguir.
  const loadRequesters = useCallback(async (phone) => {
    if (!phone) {
      setRequesters([]);
      return;
    }

    setIsLoadingRequesters(true);
    try {
      const { data } = await axios.get(
        `/requesters/by-phone/${encodeURIComponent(phone)}`
      );
      const encontrados = Array.isArray(data) ? data : [];
      setRequesters(encontrados);
      setSelectedRequesterId(
        encontrados.length === 1 ? String(encontrados[0].id) : ''
      );
    } catch (error) {
      console.error('Erro ao buscar solicitante pelo telefone:', error);
      setRequesters([]);
    } finally {
      setIsLoadingRequesters(false);
    }
  }, []);

  const abrirAviso = useCallback(
    (dados) => {
      setHandoff(dados);
      setRequesters([]);
      setSelectedRequesterId('');
      setServiceDesks([]);
      setSelectedServiceDeskId('');
      loadRequesters(dados.contactPhone);
    },
    [loadRequesters]
  );

  // Conhecida a empresa, carrega as mesas de serviço dela. Uma só entra
  // selecionada; havendo mais de uma, o agente escolhe aqui mesmo — assim a
  // tela de novo ticket já abre na mesa certa.
  useEffect(() => {
    let cancelado = false;

    const carregarMesas = async () => {
      setIsLoadingDesks(true);
      try {
        const { data } = await axios.get(
          `/servicedesks/customer/${empresaEscolhidaId}`
        );
        if (cancelado) return;
        const mesas = Array.isArray(data) ? data : [];
        setServiceDesks(mesas);
        setSelectedServiceDeskId(mesas.length === 1 ? String(mesas[0].id) : '');
      } catch (error) {
        if (cancelado) return;
        console.error('Erro ao buscar mesas de serviço da empresa:', error);
        setServiceDesks([]);
        setSelectedServiceDeskId('');
      } finally {
        if (!cancelado) setIsLoadingDesks(false);
      }
    };

    if (empresaEscolhidaId) {
      carregarMesas();
    } else {
      setServiceDesks([]);
      setSelectedServiceDeskId('');
    }

    return () => {
      cancelado = true;
    };
  }, [empresaEscolhidaId]);

  // Chegada em tempo real.
  useEffect(() => {
    const onNotification = (notification) => {
      if (notification && notification.type === 'chat_handoff') {
        abrirAviso(notification.data || {});
      }
    };

    WebSocketService.addEventListener('notification', onNotification);
    return () => {
      WebSocketService.removeEventListener('notification', onNotification);
    };
  }, [abrirAviso]);

  // Uma única consulta ao entrar no sistema, para o caso de o socket estar fora
  // do ar no instante do evento. Não há polling.
  useEffect(() => {
    if (!userId) return;

    const carregarPendentes = async () => {
      try {
        const { data } = await axios.get('/chat-handoffs/pending');
        if (Array.isArray(data) && data.length > 0) {
          abrirAviso(data[0]);
        }
      } catch (error) {
        console.error('Erro ao buscar atendimentos pendentes:', error);
      }
    };

    carregarPendentes();
  }, [userId, abrirAviso]);

  const fechar = () => {
    setHandoff(null);
    setRequesters([]);
    setSelectedRequesterId('');
  };

  const dispensar = async () => {
    if (handoff && handoff.id) {
      try {
        await axios.put(`/chat-handoffs/${handoff.id}/dismiss`);
      } catch (error) {
        console.error('Erro ao dispensar atendimento:', error);
      }
    }
    fechar();
  };

  const abrirChamado = () => {
    // O estado da rota carrega o prefill: telefone e protocolo não vão para a
    // URL, e a tela de novo ticket não precisa consultar nada de novo.
    navigate('/tickets/new', {
      state: {
        chatPrefill: {
          protocolNumber: handoff.protocolNumber,
          contactName: handoff.contactName,
          contactPhone: handoff.contactPhone,
          requesterId: solicitanteEscolhido ? solicitanteEscolhido.id : null,
          companyId: solicitanteEscolhido
            ? solicitanteEscolhido.companyId
            : null,
          serviceDeskId: selectedServiceDeskId || null,
        },
      },
    });
    fechar();
  };

  if (!handoff) return null;

  const precisaEscolherEmpresa = requesters.length > 1;

  return (
    <Modal show onHide={dispensar} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          <FaWhatsapp className="text-success me-2" />
          Atendimento assumido no chat
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-3">
          Você iniciou um atendimento com{' '}
          <strong>{handoff.contactName || 'um contato'}</strong> no chat. Deseja
          abrir um chamado?
        </p>

        <div className="small text-muted mb-3">
          <div>
            <strong>Telefone:</strong> {handoff.contactPhone || 'não informado'}
          </div>
          <div>
            <strong>Protocolo:</strong>{' '}
            <Badge bg="secondary">{handoff.protocolNumber}</Badge>
          </div>
        </div>

        {isLoadingRequesters && (
          <div className="d-flex align-items-center gap-2 small text-muted">
            <Spinner animation="border" size="sm" />
            Procurando o solicitante pelo telefone...
          </div>
        )}

        {!isLoadingRequesters && requesters.length === 0 && (
          <div className="alert alert-warning py-2 mb-0 small">
            Nenhum solicitante cadastrado com este telefone. O chamado abre com
            o telefone e o protocolo preenchidos; o restante você completa na
            tela.
          </div>
        )}

        {!isLoadingRequesters && requesters.length === 1 && (
          <div className="alert alert-success py-2 mb-0 small">
            Solicitante: <strong>{requesters[0].name}</strong> —{' '}
            {requesters[0].companyName || 'empresa não identificada'}
          </div>
        )}

        {!isLoadingRequesters && precisaEscolherEmpresa && (
          <Form.Group>
            <Form.Label className="small mb-1">
              Este contato está em mais de uma empresa. Para qual delas é o
              chamado?
            </Form.Label>
            <Form.Select
              value={selectedRequesterId}
              onChange={(e) => setSelectedRequesterId(e.target.value)}
            >
              <option value="">Selecione a empresa...</option>
              {requesters.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.companyName || 'Empresa não identificada'} — {item.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {isLoadingDesks && (
          <div className="d-flex align-items-center gap-2 small text-muted mt-3">
            <Spinner animation="border" size="sm" />
            Carregando mesas de serviço...
          </div>
        )}

        {!isLoadingDesks && solicitanteEscolhido && serviceDesks.length > 0 && (
          <Form.Group className="mt-3">
            <Form.Label className="small mb-1">
              Mesa de serviço
              {serviceDesks.length === 1 ? ' (única desta empresa)' : ''}
            </Form.Label>
            <Form.Select
              value={selectedServiceDeskId}
              onChange={(e) => setSelectedServiceDeskId(e.target.value)}
              disabled={serviceDesks.length === 1}
            >
              <option value="">Selecione a mesa...</option>
              {serviceDesks.map((desk) => (
                <option key={desk.id} value={desk.id}>
                  {desk.displayName || desk.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {!isLoadingDesks &&
          solicitanteEscolhido &&
          serviceDesks.length === 0 && (
            <div className="alert alert-warning py-2 mb-0 mt-3 small">
              Esta empresa não está vinculada a nenhuma mesa de serviço. Você
              precisará escolher a mesa na tela do chamado.
            </div>
          )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={dispensar}>
          Agora não
        </Button>
        <Button
          variant="primary"
          onClick={abrirChamado}
          disabled={
            (precisaEscolherEmpresa && !selectedRequesterId) ||
            isLoadingDesks ||
            (serviceDesks.length > 1 && !selectedServiceDeskId)
          }
        >
          Abrir chamado
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
